/** API_SPEC.md §1 — the guest funnel. */
import { API_BASE, api } from './client';
import type {
  RoleSuggestionList,
  GuestAnalysis,
  GuestAnalysisStarted,
  GuestHandoffResponse,
  GuestSessionPatch,
  GuestSessionResponse,
  GuestUploadAccepted,
} from './types';

export const guestApi = {
  /** The target role field's suggestions: generic titles the platform has jobs for, by word prefix. Public, before sign-up. */
  roles: (q: string, signal?: AbortSignal) => api<RoleSuggestionList>('/v1/guest/roles', { query: { q: q || undefined }, signal }),
  /** Creates/refreshes `cj_guest`. Idempotent per cookie; also a bot-mitigation precondition for uploads. */
  ensureSession: (body: GuestSessionPatch = {}) => api<GuestSessionResponse>('/v1/guest/session', { method: 'POST', body }),
  patchSession: (body: GuestSessionPatch) => api<GuestSessionResponse>('/v1/guest/session', { method: 'PATCH', body }),
  /**
   * Signed in only (a 401 here would sign the app out, so it is never called
   * signed out): hands the account's funnel run to the account and, when it has
   * no résumé yet, makes its first one from it — Classic, the role copied, the
   * original scored, the fixes delivered, the first Download coached. The
   * server spends the `cj_guest` cookie once the run is attached, so nothing may
   * poll the guest analysis after this resolves.
   */
  handoff: () => api<GuestHandoffResponse>('/v1/guest/handoff', { method: 'POST' }),
  upload: (file: File) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return api<GuestUploadAccepted>('/v1/guest/uploads', { method: 'POST', formData: fd });
  },
  /**
   * Starts scoring an already-uploaded résumé — the funnel's "Score my
   * resume" click, not the upload. Split out so the role question (asked
   * *after* the upload, on the same screen) has a chance to reach the
   * server before anything is scored: the analysis this creates is scored
   * against whatever role the guest session holds at this exact call, so
   * callers must patch the session with the chosen role first and await it.
   */
  startAnalysis: (uploadId: string) =>
    api<GuestAnalysisStarted>(`/v1/guest/uploads/${encodeURIComponent(uploadId)}/analyses`, { method: 'POST' }),
  /**
   * The guest report's "Delete my upload" (legal-pages ticket 04): the upload,
   * its file, its analyses, their screenshots and the cached parse. The guest
   * session survives, so the same visitor can upload again.
   *
   * 204 deleted. 404 not this guest's, or already gone: nothing of theirs is
   * left under that id, so the caller treats it as deleted. 409 with
   * `details.reason`: `busy` (a worker is mid model call; the same request
   * succeeds a few seconds later) or `account_upload` (the résumé belongs to an
   * account; nothing was deleted, and the caller must never say it was). 429
   * past 20 an hour. `silent401`: no guest session is not a signed-out app.
   */
  deleteUpload: (uploadId: string) => api<void>(`/v1/guest/uploads/${encodeURIComponent(uploadId)}`, { method: 'DELETE', silent401: true }),
  analysis: (analysisId: string) => api<GuestAnalysis>(`/v1/guest/analyses/${encodeURIComponent(analysisId)}`),
  sampleAnalysis: () => api<GuestAnalysis>('/v1/guest/analyses/sample'),
  /** Ticket 38: a plain `<img src>` URL, not a fetch — the browser's own GET
   *  carries `cj_guest`/`cj_session`, which is what the ownership check on
   *  `GET /v1/guest/analyses/{id}/preview-image/{side}` reads. Rendered only
   *  once `previewImages[side] === 'ready'`. `version` is that side's
   *  `previewImages.*Version` (ux-bug-pass ticket 08): the URL changes when
   *  the bytes do, so the route can let the browser cache it for a day. */
  previewImageUrl: (analysisId: string, side: 'before' | 'after', version?: string) =>
    `${API_BASE}/v1/guest/analyses/${encodeURIComponent(analysisId)}/preview-image/${side}`
    + (version ? `?v=${encodeURIComponent(version)}` : ''),
};
